import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Database, ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface DatabaseData {
  collection: string;
  data: any[];
  pagination: PaginationData;
}

const DatabaseViewer: React.FC = () => {
  const { collection } = useParams<{ collection: string }>();
  const [data, setData] = useState<DatabaseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const fetchData = async (page = 1) => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/database?collection=${collection}&page=${page}&limit=20`, {
        headers
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Error fetching database data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (collection) {
      fetchData(currentPage);
    }
  }, [collection, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setLoading(true);
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchData(currentPage);
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const formatValue = (value: any, key = ''): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-500">null</span>;
    }

    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-400' : 'text-red-400'}>{value.toString()}</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-blue-400">{value}</span>;
    }

    if (typeof value === 'string') {
      // Check if it's a date
      if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return <span className="text-yellow-400">{date.toLocaleString()}</span>;
          }
        } catch (e) {
          // Not a valid date, treat as string
        }
      }
      
      if (value.length > 100) {
        return (
          <span className="text-gray-300">
            {value.substring(0, 100)}...
            <button className="text-purple-400 hover:text-purple-300 ml-1">
              (show more)
            </button>
          </span>
        );
      }
      return <span className="text-gray-300">{value}</span>;
    }

    if (Array.isArray(value)) {
      return (
        <div className="text-gray-300">
          Array[{value.length}]
          <button className="text-purple-400 hover:text-purple-300 ml-1">
            (expand)
          </button>
        </div>
      );
    }

    if (typeof value === 'object') {
      return (
        <div className="text-gray-300">
          Object
          <button className="text-purple-400 hover:text-purple-300 ml-1">
            (expand)
          </button>
        </div>
      );
    }

    return <span className="text-gray-300">{String(value)}</span>;
  };

  const renderItem = (item: any, index: number) => {
    const itemId = item._id || item.id || `item-${index}`;
    const isExpanded = expandedItems.has(itemId);
    
    return (
      <div key={itemId} className="bg-gray-800 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-white">
            {collection === 'users' ? (item.facebookId || item.auth0Id || 'Unknown User') :
             collection === 'conversations' ? (item.id || 'Unknown Conversation') :
             collection === 'admins' ? (item.username || 'Unknown Admin') :
             (item.id || item._id || `Item ${index + 1}`)}
          </h3>
          <button
            onClick={() => toggleExpanded(itemId)}
            className="text-purple-400 hover:text-purple-300"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
        
        {!isExpanded ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(item).slice(0, 6).map(([key, value]) => (
              <div key={key} className="text-sm">
                <span className="text-gray-400 font-medium">{key}:</span>
                <div className="mt-1">
                  {formatValue(value, key)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(item).map(([key, value]) => (
              <div key={key} className="border-b border-gray-700 pb-2">
                <div className="flex items-start gap-2">
                  <span className="text-gray-400 font-medium min-w-0 w-1/4">{key}:</span>
                  <div className="flex-1 min-w-0">
                    {typeof value === 'object' && value !== null ? (
                      <pre className="text-xs bg-gray-700 p-2 rounded overflow-x-auto">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      formatValue(value, key)
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p>Loading database data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Database className="w-6 h-6 text-purple-400" />
              {collection} Collection
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {data ? (
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-400">
                    Total Records: <span className="text-white font-semibold">{data.pagination.total}</span>
                  </div>
                  <div className="text-sm text-gray-400">
                    Page: <span className="text-white font-semibold">{data.pagination.page}</span> of{' '}
                    <span className="text-white font-semibold">{data.pagination.pages}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>

            {/* Data */}
            <div className="space-y-4">
              {data.data.length > 0 ? (
                data.data
                  .filter(item => {
                    if (!searchTerm) return true;
                    return JSON.stringify(item).toLowerCase().includes(searchTerm.toLowerCase());
                  })
                  .map((item, index) => renderItem(item, index))
              ) : (
                <div className="bg-gray-800 rounded-lg p-8 text-center">
                  <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400">No records found in this collection</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {data.pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(data.pagination.page - 1)}
                  disabled={data.pagination.page === 1}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, data.pagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 rounded ${
                          page === data.pagination.page
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(data.pagination.page + 1)}
                  disabled={data.pagination.page === data.pagination.pages}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">Failed to load collection data</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseViewer; 